import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { PhotoCapture, type UploadedPhoto } from "@/components/mapping/PhotoCapture";
import { toast } from "sonner";

interface LocationPhoto {
  id: string;
  kind: "overview" | "degradation";
  url: string;
}

interface LocationMappingFieldsFormProps {
  locationId: string;
  publicAccess: boolean | null;
  open247: boolean | null;
  openingHours: string | null;
  passingByVisibility: number | null;
  nearbyHabitations: number | null;
  signsOfDegradation: boolean | null;
  janitors: boolean | null;
  lighting: boolean | null;
  dubiousPeople: boolean | null;
  videoCameras: boolean | null;
  locationPhotos: LocationPhoto[];
  minInstallationSpots: number;
  placedPinCount: number;
}

export function LocationMappingFieldsForm({
  locationId,
  publicAccess,
  open247,
  openingHours,
  passingByVisibility,
  nearbyHabitations,
  signsOfDegradation,
  janitors,
  lighting,
  dubiousPeople,
  videoCameras,
  locationPhotos,
  minInstallationSpots,
  placedPinCount,
}: LocationMappingFieldsFormProps) {
  const [local, setLocal] = useState({
    publicAccess,
    open247,
    openingHours: openingHours ?? "",
    passingByVisibility,
    nearbyHabitations,
    signsOfDegradation,
    janitors,
    lighting,
    dubiousPeople,
    videoCameras,
    minInstallationSpots,
  });

  useEffect(() => {
    setLocal({
      publicAccess,
      open247,
      openingHours: openingHours ?? "",
      passingByVisibility,
      nearbyHabitations,
      signsOfDegradation,
      janitors,
      lighting,
      dubiousPeople,
      videoCameras,
      minInstallationSpots,
    });
  }, [locationId, publicAccess, open247, openingHours, passingByVisibility, nearbyHabitations, signsOfDegradation, janitors, lighting, dubiousPeople, videoCameras, minInstallationSpots]);

  const utils = trpc.useUtils();
  const mutation = trpc.physicalMapping.setLocationMappingFields.useMutation({
    onSuccess: () => {
      utils.physicalMapping.getLocationDetails.invalidate({ locationId });
      utils.physicalMapping.list.invalidate();
    },
    onError: (e: { message: string }) => toast.error(`Save failed: ${e.message}`),
  });

  const uploadMutation = trpc.physicalMapping.uploadLocationPhoto.useMutation({
    onSuccess: () => {
      utils.physicalMapping.getLocationDetails.invalidate({ locationId });
    },
    onError: (e: { message: string }) => toast.error(`Upload failed: ${e.message}`),
  });

  const deleteMutation = trpc.physicalMapping.deleteLocationPhoto.useMutation({
    onSuccess: () => {
      utils.physicalMapping.getLocationDetails.invalidate({ locationId });
    },
    onError: (e: { message: string }) => toast.error(`Delete failed: ${e.message}`),
  });

  const save = (patch: Partial<typeof local>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    mutation.mutate({
      locationId,
      publicAccess: next.publicAccess,
      open247: next.open247,
      openingHours: next.openingHours?.trim() ? next.openingHours.trim() : null,
      passingByVisibility: next.passingByVisibility,
      nearbyHabitations: next.nearbyHabitations,
      signsOfDegradation: next.signsOfDegradation,
      janitors: next.janitors,
      lighting: next.lighting,
      dubiousPeople: next.dubiousPeople,
      videoCameras: next.videoCameras,
      minInstallationSpots: next.minInstallationSpots,
    });
  };

  const overviewPhotos: UploadedPhoto[] = locationPhotos
    .filter((p) => p.kind === "overview")
    .map((p) => ({ id: p.id, url: p.url }));

  const degradationPhotos: UploadedPhoto[] = locationPhotos
    .filter((p) => p.kind === "degradation")
    .map((p) => ({ id: p.id, url: p.url }));

  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Location-level
      </h3>

      <div className="flex items-center justify-between">
        <Label className="text-sm">Public access</Label>
        <Switch
          checked={local.publicAccess === true}
          onCheckedChange={(v) => save({ publicAccess: v })}
        />
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-sm">Open 24/7</Label>
        <Switch
          checked={local.open247 === true}
          onCheckedChange={(v) => save({ open247: v })}
        />
      </div>

      {local.open247 !== true && (
        <div className="space-y-1">
          <Label className="text-sm">Opening hours</Label>
          <Textarea
            value={local.openingHours}
            placeholder="e.g. Mon–Fri 8–22, Sat 9–18"
            onChange={(e) =>
              setLocal((s) => ({ ...s, openingHours: e.target.value }))
            }
            onBlur={() => save({ openingHours: local.openingHours })}
            className="text-sm min-h-[60px]"
          />
        </div>
      )}

      {/* Minimum installation spots */}
      <div className="space-y-2 pt-1">
        <Label className="text-sm">Minimum installation spots</Label>
        <p className="text-xs text-muted-foreground">
          The mapper must end up with at least this many installation spots (including ones you've already placed).
        </p>
        <div className="flex items-center justify-center gap-4 pt-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => {
              const next = Math.max(Math.max(1, placedPinCount), local.minInstallationSpots - 1);
              save({ minInstallationSpots: next });
            }}
            disabled={local.minInstallationSpots <= Math.max(1, placedPinCount)}
            aria-label="Decrease minimum"
          >
            <Minus className="h-5 w-5" />
          </Button>
          <span className="w-12 text-center text-3xl font-bold tabular-nums">
            {local.minInstallationSpots}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => {
              const next = Math.min(50, local.minInstallationSpots + 1);
              save({ minInstallationSpots: next });
            }}
            disabled={local.minInstallationSpots >= 50}
            aria-label="Increase minimum"
          >
            <Plus className="h-5 w-5" />
          </Button>
        </div>
        {local.minInstallationSpots < placedPinCount && (
          <p className="text-xs text-destructive">
            Cannot be less than {placedPinCount} placed pins
          </p>
        )}
      </div>

      {/* Overview photos */}
      <div className="space-y-1 pt-1">
        <Label className="text-sm">
          Overview photos
          <span className="ml-1 text-xs text-muted-foreground font-normal">
            ({overviewPhotos.length})
          </span>
        </Label>
        <PhotoCapture
          photos={overviewPhotos}
          onUpload={async (base64) => {
            await uploadMutation.mutateAsync({ locationId, kind: "overview", base64 });
          }}
          onDelete={async (photoId) => {
            await deleteMutation.mutateAsync({ photoId });
          }}
          minRequired={3}
        />
      </div>

      {/* Sliders */}
      <div className="space-y-2 pt-1">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Passing-by visibility</Label>
            <span className="text-xs text-muted-foreground">
              {local.passingByVisibility ?? "–"}/5
            </span>
          </div>
          <Slider
            min={1}
            max={5}
            step={1}
            value={[local.passingByVisibility ?? 3]}
            onValueChange={(v) => save({ passingByVisibility: v[0] })}
            className="mt-2"
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Nearby habitations</Label>
            <span className="text-xs text-muted-foreground">
              {local.nearbyHabitations ?? "–"}/5
            </span>
          </div>
          <Slider
            min={1}
            max={5}
            step={1}
            value={[local.nearbyHabitations ?? 3]}
            onValueChange={(v) => save({ nearbyHabitations: v[0] })}
            className="mt-2"
          />
        </div>
      </div>

      {/* Switches */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
        <SwitchRow
          label="Signs of degradation"
          value={local.signsOfDegradation}
          onChange={(v) => save({ signsOfDegradation: v })}
        />
        <SwitchRow
          label="Janitors"
          value={local.janitors}
          onChange={(v) => save({ janitors: v })}
        />
        <SwitchRow
          label="Lighting"
          value={local.lighting}
          onChange={(v) => save({ lighting: v })}
        />
        <SwitchRow
          label="Dubious people"
          value={local.dubiousPeople}
          onChange={(v) => save({ dubiousPeople: v })}
        />
        <SwitchRow
          label="Video cameras"
          value={local.videoCameras}
          onChange={(v) => save({ videoCameras: v })}
        />
      </div>

      {/* Degradation photos (conditional) */}
      {local.signsOfDegradation === true && (
        <div className="space-y-1">
          <Label className="text-sm">
            Degradation photos
            <span className="ml-1 text-xs text-muted-foreground font-normal">
              ({degradationPhotos.length})
            </span>
          </Label>
          <PhotoCapture
            photos={degradationPhotos}
            onUpload={async (base64) => {
              await uploadMutation.mutateAsync({ locationId, kind: "degradation", base64 });
            }}
            onDelete={async (photoId) => {
              await deleteMutation.mutateAsync({ photoId });
            }}
          />
        </div>
      )}
    </div>
  );
}

function SwitchRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-sm">{label}</Label>
      <Switch
        checked={value === true}
        onCheckedChange={onChange}
      />
    </div>
  );
}
